import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useState, useEffect } from 'react';
import { Select, useLocalizeMessage, Table, Button, useToastNotification } from 'libs.nucleus';
import { DataTransferTemplateContext } from '../../../contexts/data_transfer_template/data_transfer_template.context';
const computeOptions = (options, exclude) => {
    const newOptions = options.filter((item) => item !== exclude).map((item) => ({ label: item, value: item }));
    return newOptions;
};
export const CreateTransferTemplatePivotStep = () => {
    const translate = useLocalizeMessage();
    const { addNotification } = useToastNotification();
    const { availableColumns, createTemplateFormData, setCreateTemplateFormData, fetchPivotPreviewValues, isReadOnly } = useContext(DataTransferTemplateContext);
    const [pivotBy, setPivotBy] = useState();
    const [pivotValue, setPivotValue] = useState();
    const [hasInteracted, setHasInteracted] = useState(false);
    const [pivotByOptions, setPivotByOptions] = useState(computeOptions(availableColumns));
    const [pivotValueOptions, setPivotValueOptions] = useState(computeOptions(availableColumns));
    // Prefill from the existing template py when available (edit mode)
    useEffect(() => {
        const py = createTemplateFormData?.py;
        const params = py?.params || {};
        // Only prefill if we have py and no local selections yet
        if (py && (!pivotBy || !pivotValue)) {
            const savedPivotBy = Array.isArray(params.columns) && params.columns.length ? params.columns[0] : undefined;
            const savedValue = params.values;
            if (savedPivotBy && !pivotBy) {
                setPivotBy({ label: savedPivotBy, value: savedPivotBy });
            }
            if (savedValue && !pivotValue) {
                setPivotValue({ label: savedValue, value: savedValue });
            }
        }
        // Do not mark as interacted when pre-filling
    }, [createTemplateFormData]);
    // When pivot selection changes, update the template Python transformation (py)
    useEffect(() => {
        const selectedColumn = pivotBy?.value;
        const selectedValue = pivotValue?.value;
        // If pivot value becomes same as pivot column, clear the value selection
        if (selectedColumn && pivotValue?.value === selectedColumn) {
            setPivotValue(undefined);
            return;
        }
        if (selectedColumn && selectedValue && availableColumns.length) {
            setCreateTemplateFormData({
                ...createTemplateFormData,
                py: {
                    operation: 'pivot_table',
                    params: {
                        columns: [selectedColumn],
                        index: indexCols,
                        values: selectedValue,
                    },
                    func_params: {},
                },
            });
        }
        else {
            // Clear Python transformation only after user interaction to avoid wiping existing config on mount
            if (hasInteracted &&
                !selectedColumn &&
                !selectedValue &&
                Boolean(createTemplateFormData?.py?.operation)) {
                setCreateTemplateFormData({
                    ...createTemplateFormData,
                    py: undefined,
                });
            }
        }
    }, [pivotBy, pivotValue, availableColumns, hasInteracted]);
    // Preview columns mapped to be displayed in the table (only index columns)
    const indexCols = (() => {
        const selectedColumn = pivotBy?.value;
        const selectedValue = pivotValue?.value;
        return availableColumns.filter((c) => c !== selectedColumn && c !== selectedValue);
    })();
    const previewColumns = indexCols.map((key) => ({
        accessorKey: key,
        header: key,
    }));
    // Preview table for available columns
    const [pivotPreviewValues, setPivotPreviewValues] = useState([]);
    // if pivotBy is changed, update pivotValueOptions
    useEffect(() => {
        if (pivotBy?.value) {
            setPivotValueOptions(computeOptions(availableColumns, pivotBy?.value));
        }
    }, [pivotBy]);
    // If availableColumns changes, clear pivot selection
    // as the available columns may have changed due to a new study being selected
    useEffect(() => {
        if (availableColumns && availableColumns.length > 0) {
            setPivotBy(undefined);
            setPivotValue(undefined);
            setPivotByOptions(computeOptions(availableColumns));
            setPivotValueOptions(computeOptions(availableColumns, pivotBy?.value));
        }
    }, [availableColumns]);
    const getPivotPreviewValues = async () => {
        try {
            if (pivotBy?.value) {
                const previewValues = await fetchPivotPreviewValues(pivotBy.value);
                const additionalPreviewValues = previewValues.map((key) => ({ accessorKey: key, header: key }));
                setPivotPreviewValues([...previewColumns, ...additionalPreviewValues]);
            }
        }
        catch (error) {
            addNotification({
                title: translate('Unable to fetch preview values'),
                subtitle: translate('Please try again later.'),
                type: 'warning',
            });
        }
    };
    return (_jsxs("div", { className: 'space-y-6 size-full', children: [_jsxs("div", { className: 'space-y-4', children: [_jsx("div", { className: 'text-lg font-semibold', children: translate('Pivot settings') }), _jsx(Select, { label: translate('Pivot by'), placeholder: pivotByOptions.length ? translate('Select a column') : translate('No columns available'), options: pivotByOptions, value: pivotBy, onChange: (option) => {
                            setHasInteracted(true);
                            setPivotBy(option);
                        }, width: 'full', disabled: pivotByOptions.length === 0 || isReadOnly, searchableOptions: true }), _jsx(Select, { label: translate('Value'), placeholder: pivotValueOptions.length ? translate('Select value column') : translate('No columns available'), options: pivotValueOptions, value: pivotValue, onChange: (option) => {
                            setHasInteracted(true);
                            setPivotValue(option);
                        }, width: 'full', disabled: pivotValueOptions.length === 0 || isReadOnly, searchableOptions: true })] }), _jsx(Button, { label: 'Preview', onClick: getPivotPreviewValues, size: 'base', variant: 'secondary', disabled: !pivotBy?.value || !pivotValue?.value }), pivotBy?.value && pivotValue?.value && pivotPreviewValues.length > 0 && (_jsxs("div", { className: 'space-y-2', children: [_jsx("div", { className: 'text-lg font-semibold', children: translate('Preview') }), _jsx(Table, { columns: pivotPreviewValues, data: [], labelNoResults: '' })] }))] }));
};
